import React, { useState, useContext, useEffect } from 'react';
import { Box, Button, FormControl, InputLabel, LinearProgress, MenuItem, Select, TextField } from '@mui/material';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import DialogGlynt from '../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../common/Dialog/DialogActionsGlynt';
import mapTokenizerVersion from '../../utils/mapTokenizerVersion';
import useGlynt from '../hooks/useGlynt';
import useOCREngines from '../hooks/useOCREngines';
import { AppContext } from '../../App';
import { IOrg } from '../../types/my-types';
import errorResponses from '../../errorResponses';

const OrgTokenizerDialog: React.FC = () => {
  const { org, orgLabel, setOrgLabel, setOrg, setToast, isStaff } = useContext(AppContext);
  const [open, setOpen] = useState(false);
  const [version, setVersion] = useState(org?.default_tokenizer_version.name);
  const [label, setLabel] = useState(orgLabel);
  const { data, isLoading, isError } = useOCREngines();

  const glynt = useGlynt();
  const client = useQueryClient();

  useEffect(() => {
    if (!open) return;
    setLabel(orgLabel);
    setVersion(org?.default_tokenizer_version.name);
  }, [open, orgLabel, org?.default_tokenizer_version.name]);

  useEffect(() => {
    if (isError) {
      setToast({ variant: 'error', open: true, message: errorResponses.failedToUpdateOCR });
    }
  }, [isError, setToast]);

  const updateOrg = useMutation({
    mutationFn: async () => {
      const updates: Record<string, unknown> = {};
      if (version !== org?.default_tokenizer_version.name) {
        updates.default_tokenizer_version = version === 'latest' ? null : version;
      }
      if (label !== orgLabel) {
        updates.label = label;
      }
      return (await glynt).org.update(org?.id as string, updates);
    },
    onSuccess: (org) => {
      setOrg(org as unknown as IOrg);
      if (label !== orgLabel) setOrgLabel(label);
      client.invalidateQueries({ queryKey: ['orgs'] });
      client.invalidateQueries({ queryKey: ['datapools'] });
      setToast({ variant: 'success', open: true, message: 'Organization settings updated' });
      setOpen(false);
    },
    onError: () => {
      setToast({ variant: 'error', open: true, message: errorResponses.failedToUpdateOCR });
    },
  });

  const handleClose = () => {
    if (!updateOrg.isPending) {
      setLabel(orgLabel);
      setVersion(org?.default_tokenizer_version.name);
      setOpen(false);
    }
  };

  const hasChanges =
    (version !== org?.default_tokenizer_version.name && !!version) ||
    (label.trim().length > 0 && label !== orgLabel);

  if (isLoading) {
    return <LinearProgress />;
  }

  if (isError) {
    return (
      <Button
        variant="outlined"
        disabled
        data-cy="btn-org-settings"
        autoFocus={false}
        startIcon={<TuneOutlinedIcon sx={{ fontSize: '16px !important' }} />}
      >
        Settings
      </Button>
    );
  }

  return (
    <>
      <Button
        variant="outlined"
        onClick={() => setOpen(true)}
        data-cy="btn-org-settings"
        autoFocus={false}
        startIcon={<TuneOutlinedIcon sx={{ fontSize: '16px !important' }} />}
      >
        Settings
      </Button>
      <DialogGlynt open={open} closeModal={handleClose} cypressId="dialog-org-settings" loading={updateOrg.isPending}>
        <DialogTitleGlynt title="Organization Settings" closeModal={handleClose} loading={updateOrg.isPending} />
        <DialogContentGlynt>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              autoComplete="off"
              data-cy="input-rename-org"
              label="Organization Name"
              variant="outlined"
              size="small"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              autoFocus
              sx={{ flex: 1 }}
            />
            <FormControl variant="outlined" size="small" sx={{ flex: 1 }}>
              <InputLabel id="ocr-engine-select-label">OCR Engine</InputLabel>
              <Select
                labelId="ocr-engine-select-label"
                value={version ?? ''}
                variant="outlined"
                label="OCR Engine"
                data-cy="ocr-selector"
                onChange={(e) => setVersion(e.target.value as string)}
              >
                {data?.active.map((tv) => (
                  <MenuItem key={tv} value={tv} data-cy="ocr-selector-item-current">
                    {mapTokenizerVersion(tv)}
                  </MenuItem>
                ))}
                {isStaff &&
                  data?.experimental.map((tv) => (
                    <MenuItem key={tv} value={tv} data-cy="ocr-selector-item">
                      {mapTokenizerVersion(tv)} (beta)
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContentGlynt>
        <DialogActionsGlynt
          handleSubmit={() => updateOrg.mutate()}
          closeModal={handleClose}
          cancelCypressId="org-dialog-cancel"
          submitCypressId="org-dialog-submit"
          clearCypressId=""
          submitButtonText="Save"
          submitDisabled={!hasChanges || label.trim().length === 0}
          submitLoading={updateOrg.isPending}
        />
      </DialogGlynt>
    </>
  );
};

export default OrgTokenizerDialog;
